import type { RawHeroStats, TierInfo } from "$lib/types";

export function slugify(name: string): string {
    return name
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/(^-|-$)/g, "");
}

/** Strip diacritics so e.g. "lucio" matches "Lúcio". */
export function stripAccents(s: string): string {
    return s.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
}

// ---------------------------------------------------------------------------
// URL query-param slug helpers
// ---------------------------------------------------------------------------

/** Resolve a lowercase tier slug from a URL param back to its canonical key (e.g. "master" → "Master"). */
export function tierFromSlug(slug: string | null, tiers: TierInfo[], fallback = "All"): string {
    if (!slug) return fallback;
    // "silver-plus" → "Silver+" — check for plus-tier slugs first
    const PLUS_SLUG = "-plus";
    if (slug.endsWith(PLUS_SLUG)) {
        const baseSlug = slug.slice(0, -PLUS_SLUG.length);
        const baseTier = tiers.find((t) => slugify(t.key) === slugify(baseSlug));
        if (baseTier) return baseTier.key + "+";
        // The "+" tier itself may be in the tiers list already
        const plusTier = tiers.find((t) => t.key.endsWith("+") && slugify(t.key.slice(0, -1)) === slugify(baseSlug));
        if (plusTier) return plusTier.key;
    }
    return tiers.find((t) => slugify(t.key) === slugify(slug))?.key ?? fallback;
}

/** Format a patch date (YYYY-MM-DD) for display, e.g. "Jun 10". */
export function formatPatchDate(dateStr: string): string {
    const d = new Date(dateStr + "T00:00:00");
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

/** Convert a tier key to its URL-safe slug form. */
export function tierToSlug(key: string): string {
    // "Silver+" → "silver-plus" so the "+" doesn't get stripped by slugify
    if (key.endsWith("+")) return slugify(key.slice(0, -1)) + "-plus";
    return slugify(key);
}

export type SortKey = "winRate" | "pickRate" | "banRate" | "pickBan" | "name" | "tier";

const SORT_SLUG_TO_KEY: Record<string, SortKey> = {
    "win-rate": "winRate",
    "pick-rate": "pickRate",
    "ban-rate": "banRate",
    "pick-ban": "pickBan",
    name: "name",
    tier: "tier",
};

const SORT_KEY_TO_SLUG: Record<string, string> = {
    winRate: "win-rate",
    pickRate: "pick-rate",
    banRate: "ban-rate",
    pickBan: "pick-ban",
    name: "name",
    tier: "tier",
};

/** Resolve a sort slug from a URL param back to its internal key. */
export function sortFromSlug(slug: string | null, fallback: SortKey = "winRate"): SortKey {
    if (!slug) return fallback;
    return SORT_SLUG_TO_KEY[slug] ?? fallback;
}

/** Convert an internal sort key to its URL-safe slug form. */
export function sortToSlug(key: string): string {
    return SORT_KEY_TO_SLUG[key] ?? key;
}

const TIER_RANK: Record<string, number> = { S: 5, A: 4, B: 3, C: 2, D: 1, "—": 0 };

/** Compare two tier strings (S > A > B > C > D > —). */
export function compareTier(a: string, b: string): number {
    return (TIER_RANK[a] ?? 0) - (TIER_RANK[b] ?? 0);
}

/**
 * Returns true when the value is Blizzard's "insufficient data" sentinel.
 * Blizzard returns -1 on a 0-100 scale, which becomes -0.01 after /100.
 */
export function isInsufficientData(value: number | null | undefined): boolean {
    if (value === null || value === undefined) return false;
    return value < 0;
}

/**
 * Format a 0–1 rate as a percent string, e.g. `formatPercent(0.234) === "23.40%"`.
 *
 * Returns `—` for:
 * - `null` / `undefined` — no data recorded for this metric (e.g. ban rate
 *   in legacy snapshots predating ban-rate tracking).
 * - Blizzard's insufficient-data sentinel (negative values, typically -0.01).
 */
export function formatPercent(value: number | null | undefined): string {
    if (value === null || value === undefined) return "—";
    if (isInsufficientData(value)) return "—";
    return `${(value * 100).toFixed(2)}%`;
}

/** Map of tier → CSS custom-property name defined in app.css @theme block. */
const TIER_CSS_VAR: Record<string, string> = {
    S: "var(--color-tier-s)",
    A: "var(--color-tier-a)",
    B: "var(--color-tier-b)",
    C: "var(--color-tier-c)",
    D: "var(--color-tier-d)",
};

/**
 * Returns a CSS color value for the given tier letter.
 * Uses CSS custom properties so changing the palette in app.css
 * automatically updates every tier badge / inline style.
 */
export function getTierColor(tier: string): string {
    return TIER_CSS_VAR[tier] ?? "#888888";
}

// ---------------------------------------------------------------------------
// Relative stat coloring
// ---------------------------------------------------------------------------

/**
 * Returns a Tailwind text-color class based on where `value` falls
 * between `min` and `max`.  Green = relatively high, red = relatively low.
 */
export function getTextColorClass(value: number, min: number, max: number): string {
    if (isInsufficientData(value)) return "text-base-content/30!";
    if (max === min) return "text-base-content!";
    const percent = (value - min) / (max - min);
    if (percent >= 0.8) return "text-success!";
    if (percent >= 0.6) return "text-[#77c879]!";
    if (percent >= 0.4) return "text-warning!";
    if (percent >= 0.2) return "text-[#e69052]!";
    return "text-error!";
}

/**
 * Returns a Tailwind text-color class on a white-to-green scale.
 * Suitable for neutral metrics like pick rate where low isn't inherently bad.
 */
export function getPickRateColorClass(value: number, min: number, max: number): string {
    if (isInsufficientData(value)) return "text-base-content/30!";
    if (max === min) return "text-base-content!";
    const percent = (value - min) / (max - min);
    if (percent >= 0.8) return "text-success!";
    if (percent >= 0.6) return "text-[#77c879]!";
    if (percent >= 0.4) return "text-[#a8c8a8]!";
    if (percent >= 0.2) return "text-[#95ad95]!";
    return "text-[#89a089]!";
}

/**
 * Returns a Tailwind text-color class on a muted-to-red scale.
 * Suitable for ban rate where higher = more notable / "menace."
 * Heroes with no data (`null`) or insufficient-data sentinel render muted.
 */
export function getBanRateColorClass(value: number | null | undefined, min: number, max: number): string {
    if (value === null || value === undefined) return "text-base-content/30!";
    if (isInsufficientData(value)) return "text-base-content/30!";
    if (max === min) return "text-base-content!";
    const percent = (value - min) / (max - min);
    if (percent >= 0.8) return "text-error!";
    if (percent >= 0.6) return "text-[#e69052]!";
    if (percent >= 0.4) return "text-warning!";
    if (percent >= 0.2) return "text-[#b8a878]!";
    return "text-base-content/60!";
}

/** Compute [min, max] for a numeric stat across a hero array, excluding insufficient-data sentinels and null values. */
export function getStatRange<K extends keyof RawHeroStats>(heroes: Pick<RawHeroStats, K>[], key: K): [number, number] {
    if (heroes.length === 0) return [0, 1];
    const values = heroes
        .map((h) => h[key] as number | null)
        .filter((v): v is number => v !== null && v !== undefined && !isInsufficientData(v));
    if (values.length === 0) return [0, 1];
    return [Math.min(...values), Math.max(...values)];
}

/**
 * Minimum absolute win-rate delta (decimal) to show any trend indicator.
 * "All Tiers" competitive aggregates across all ranks — smaller swings are meaningful.
 * Individual tiers and quickplay have more variance — higher threshold reduces noise.
 */
const TREND_MINOR_AGG = 0.01;
const TREND_MINOR_TIER = 0.015;
/** Absolute win-rate delta at which the indicator upgrades to a double arrow. */
const TREND_MAJOR_AGG = 0.02;
const TREND_MAJOR_TIER = 0.03;

/** Returns "▲▲"/"▲" or "▼▼"/"▼" based on magnitude, or null if below threshold.
 *  @param isAggregate true for competitive "All Tiers" (tighter thresholds) */
export function trendArrow(delta: number, isAggregate = false): string | null {
    const abs = Math.abs(delta);
    const minor = isAggregate ? TREND_MINOR_AGG : TREND_MINOR_TIER;
    const major = isAggregate ? TREND_MAJOR_AGG : TREND_MAJOR_TIER;
    if (abs < minor) return null;
    const double = abs >= major;
    return delta > 0 ? (double ? "▲▲" : "▲") : double ? "▼▼" : "▼";
}
